import { HttpClient } from '@wix/sdk-types';
import {
  AuthorizeChargeWithSavedPaymentMethodOptions,
  AuthorizeChargeWithSavedPaymentMethodResponse,
  AuthorizeChargeWithSavedPaymentMethodResponseNonNullableFields,
  CalculateRefundOptions,
  CalculateRefundResponse,
  CalculateRefundResponseNonNullableFields,
  CaptureAuthorizedPaymentsOptions,
  CaptureAuthorizedPaymentsResponse,
  CaptureAuthorizedPaymentsResponseNonNullableFields,
  GenerateReceiptsOptions,
  GenerateReceiptsResponse,
  GenerateReceiptsResponseNonNullableFields,
  GetOrderRefundabilityResponse,
  GetOrderRefundabilityResponseNonNullableFields,
  RefundPaymentsOptions,
  RefundPaymentsResponse,
  RefundPaymentsResponseNonNullableFields,
  VoidAuthorizedPaymentsOptions,
  VoidAuthorizedPaymentsResponse,
  VoidAuthorizedPaymentsResponseNonNullableFields,
  authorizeChargeWithSavedPaymentMethod as universalAuthorizeChargeWithSavedPaymentMethod,
  calculateRefund as universalCalculateRefund,
  captureAuthorizedPayments as universalCaptureAuthorizedPayments,
  generateReceipts as universalGenerateReceipts,
  getOrderRefundability as universalGetOrderRefundability,
  refundPayments as universalRefundPayments,
  voidAuthorizedPayments as universalVoidAuthorizedPayments,
} from './ecom-v1-order-billing-order-billing.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ecom' };

export function getOrderRefundability(
  httpClient: HttpClient
): GetOrderRefundabilitySignature {
  return (orderId: string) =>
    universalGetOrderRefundability(
      orderId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetOrderRefundabilitySignature {
  /**
   * Retrieves refund information about an order's payments, line items, shipping, and additional fees.
   *
   * The following information is returned:
   * - Whether a payment is refundable; previously refunded amounts; possible refund amounts for each payment.
   * - Refundable and previously refunded quantities for each line item, as well as shipping and additional fees.
   * @param - Order ID.
   */
  (orderId: string): Promise<
    GetOrderRefundabilityResponse &
      GetOrderRefundabilityResponseNonNullableFields
  >;
}

export function calculateRefund(
  httpClient: HttpClient
): CalculateRefundSignature {
  return (orderId: string, options?: CalculateRefundOptions) =>
    universalCalculateRefund(
      orderId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CalculateRefundSignature {
  /**
   * Calculates refund amounts for a given list of an order's line items, shipping, and additional fees.
   * @param - Order ID.
   */
  (orderId: string, options?: CalculateRefundOptions | undefined): Promise<
    CalculateRefundResponse & CalculateRefundResponseNonNullableFields
  >;
}

export function refundPayments(
  httpClient: HttpClient
): RefundPaymentsSignature {
  return (orderId: string, options?: RefundPaymentsOptions) =>
    universalRefundPayments(
      orderId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RefundPaymentsSignature {
  /**
   * Calls relevant payment providers and creates refund transactions for the requested payments.
   *
   * Refunds marked with `paymentRefunds.externalRefund = true` do not call payment providers and
   * only update the order's transactions records.
   * @param - Order ID.
   */
  (orderId: string, options?: RefundPaymentsOptions | undefined): Promise<
    RefundPaymentsResponse & RefundPaymentsResponseNonNullableFields
  >;
}

export function authorizeChargeWithSavedPaymentMethod(
  httpClient: HttpClient
): AuthorizeChargeWithSavedPaymentMethodSignature {
  return (
    orderId: string,
    options: AuthorizeChargeWithSavedPaymentMethodOptions
  ) =>
    universalAuthorizeChargeWithSavedPaymentMethod(
      orderId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AuthorizeChargeWithSavedPaymentMethodSignature {
  /**
   * Authorizes a payment using an order's saved payment method.
   *
   * Use the `delayedCaptureSettings` field to specify whether the payment should also be captured/voided immediately or at a later time.
   * Alternatively, the payment can be manually captured or voided using the Capture Authorized Payments or Void Authorized Payments methods.
   * @param - Order ID.
   *
   * The order must have a saved payment method associated.
   * Pass the order ID to the List Transactions For Single Order method and search the response for a payment with `regularPaymentDetails.savedPaymentMethod: true`.
   * If no saved payment exists, the call will fail.
   */
  (
    orderId: string,
    options: AuthorizeChargeWithSavedPaymentMethodOptions
  ): Promise<
    AuthorizeChargeWithSavedPaymentMethodResponse &
      AuthorizeChargeWithSavedPaymentMethodResponseNonNullableFields
  >;
}

export function voidAuthorizedPayments(
  httpClient: HttpClient
): VoidAuthorizedPaymentsSignature {
  return (orderId: string, options: VoidAuthorizedPaymentsOptions) =>
    universalVoidAuthorizedPayments(
      orderId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface VoidAuthorizedPaymentsSignature {
  /**
   * Voids an order's authorized payments.
   * @param - Order ID.
   */
  (orderId: string, options: VoidAuthorizedPaymentsOptions): Promise<
    VoidAuthorizedPaymentsResponse &
      VoidAuthorizedPaymentsResponseNonNullableFields
  >;
}

export function captureAuthorizedPayments(
  httpClient: HttpClient
): CaptureAuthorizedPaymentsSignature {
  return (orderId: string, options: CaptureAuthorizedPaymentsOptions) =>
    universalCaptureAuthorizedPayments(
      orderId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CaptureAuthorizedPaymentsSignature {
  /**
   * Captures an order's authorized payments.
   * @param - Order ID.
   */
  (orderId: string, options: CaptureAuthorizedPaymentsOptions): Promise<
    CaptureAuthorizedPaymentsResponse &
      CaptureAuthorizedPaymentsResponseNonNullableFields
  >;
}

/** @internal */
export function generateReceipts(
  httpClient: HttpClient
): GenerateReceiptsSignature {
  return (orderId: string, options: GenerateReceiptsOptions) =>
    universalGenerateReceipts(
      orderId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GenerateReceiptsSignature {
  /**
   * Generates receipts for an order's specified payments.
   * @param - Order ID.
   */
  (orderId: string, options: GenerateReceiptsOptions): Promise<
    GenerateReceiptsResponse & GenerateReceiptsResponseNonNullableFields
  >;
}

export {
  ActionEvent,
  ActionType,
  AdditionalFeeRefund,
  AdditionalFeeRefundability,
  AggregatedRefundSummary,
  ApplicationError,
  AuthorizationActionFailureDetails,
  AuthorizationCapture,
  AuthorizationCaptureStatus,
  AuthorizationDetails,
  AuthorizationVoid,
  AuthorizationVoidStatus,
  AuthorizeChargeWithSavedPaymentMethodOptions,
  AuthorizeChargeWithSavedPaymentMethodRequest,
  AuthorizeChargeWithSavedPaymentMethodResponse,
  AuthorizeChargeWithSavedPaymentMethodResponseNonNullableFields,
  CalculateRefundOptions,
  CalculateRefundRequest,
  CalculateRefundResponse,
  CalculateRefundResponseNonNullableFields,
  CalculateRefundResponseStatusOneOf,
  CalculatedAdditionalFeeRefund,
  CalculatedLineItemRefund,
  CalculatedRefundItems,
  CalculatedRefundNotAvailableStatus,
  CalculatedRefundSummary,
  CalculatedShippingRefund,
  CalculationError,
  CalculationErrorItemOneOf,
  CaptureAuthorizedPaymentsOptions,
  CaptureAuthorizedPaymentsRequest,
  CaptureAuthorizedPaymentsResponse,
  CaptureAuthorizedPaymentsResponseNonNullableFields,
  CreditCardPaymentMethodDetails,
  DelayedCaptureSettings,
  Details,
  DetailsKindOneOf,
  DomainEvent,
  DomainEventBodyOneOf,
  Duration,
  DurationUnit,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ExternalReceiptInfo,
  FieldViolation,
  GenerateReceiptsOptions,
  GenerateReceiptsRequest,
  GenerateReceiptsResponse,
  GenerateReceiptsResponseNonNullableFields,
  GetOrderRefundabilityRequest,
  GetOrderRefundabilityResponse,
  GetOrderRefundabilityResponseNonNullableFields,
  GiftCardPaymentDetails,
  LineItemRefund,
  LineItemRefundSummary,
  LineItemRefundability,
  ManuallyRefundablePaymentStatus,
  ManuallyRefundableReason,
  MembershipName,
  MembershipPaymentDetails,
  MembershipPaymentStatus,
  MembershipPaymentSummary,
  MonetaryPaymentSummary,
  NonRefundablePaymentStatus,
  NonRefundableReason,
  NotificationSideEffects,
  OrderBilling,
  OrderTransactions,
  Payment,
  PaymentCapture,
  PaymentInfo,
  PaymentInfoPaymentMethodDetailsOneOf,
  PaymentInfoSummaryOneOf,
  PaymentNotRefundableDetails,
  PaymentPaymentDetailsOneOf,
  PaymentReceiptInfoOneOf,
  PaymentRefund,
  PaymentRefundability,
  PaymentRefundabilityStatusOneOf,
  PaymentsSummary,
  Price,
  Reason,
  ReceiptInfo,
  Refund,
  RefundDetails,
  RefundItem,
  RefundItems,
  RefundItemsBreakdown,
  RefundPaymentsOptions,
  RefundPaymentsRequest,
  RefundPaymentsResponse,
  RefundPaymentsResponseNonNullableFields,
  RefundSideEffects,
  RefundStatus,
  RefundStatusInfo,
  RefundTransaction,
  RegularPaymentDetails,
  RegularPaymentDetailsPaymentMethodDetailsOneOf,
  RestockItem,
  RestockSideEffects,
  RestoreInfo,
  RuleType,
  ScheduledAction,
  ShippingRefund,
  ShippingRefundability,
  SystemError,
  TransactionStatus,
  V1AdditionalFeeRefund,
  V1AggregatedRefundSummary,
  V1LineItemRefund,
  V1ScheduledAction,
  V1ShippingRefund,
  ValidationError,
  VoidAuthorizedPaymentsOptions,
  VoidAuthorizedPaymentsRequest,
  VoidAuthorizedPaymentsResponse,
  VoidAuthorizedPaymentsResponseNonNullableFields,
  WixReceiptInfo,
} from './ecom-v1-order-billing-order-billing.universal.js';
